import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PerfilImagen.css';

const formatCount = (num) => {
  const n = Number(num) || 0;
  if (n >= 1000000) {
    return Math.round(n / 1000000) + ' M';
  }
  return n.toLocaleString('es-CL');
};

const LikeBadge = ({ liked, count, onClick }) => (
  <div className="badge-wrap">
    <div className="badge" onClick={onClick}>
      <div className="icon-row">
        <i className={`fas fa-heart heart${liked ? ' liked' : ''}`}></i>
        <span className="num">{formatCount(count)}</span>
      </div>
    </div>
  </div>
);

const PerfilImagen = ({ data, isEditMode = false, onSave }) => {
  const navigate = useNavigate();
  const [fotos, setFotos] = useState(Array(10).fill(null));
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likesState, setLikesState] = useState({});
  const [showLikeLoginBox, setShowLikeLoginBox] = useState(false);
  const [creadorId, setCreadorId] = useState(null);

  const isUserLoggedIn = !!localStorage.getItem('userToken');

  // Detectar sesión activa de creadora (login normal o vía Inspeccionar de Admin)
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${process.env.REACT_APP_AUTH_CREADOR_URL}/check_session.php`, {
      credentials: 'include',
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.success && data?.user_id) {
          setCreadorId(data.user_id);
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

useEffect(() => {
  console.log('=== PerfilImagen useEffect INICIADO ===');
  console.log('data recibido:', data);
  const controller = new AbortController();
  (async () => {
    try {
      setLoading(true);
      setError(null);
      const profileId = data?.id ?? data?.profile_id ?? data?.perfil_id;
      console.log('📌 profileId extraído:', profileId);
      if (!profileId) {
        const errorMsg = 'No se encontró ID de perfil en data: ' + JSON.stringify(data);
        console.error('❌', errorMsg);
        throw new Error(errorMsg);
      }
      const url = `${process.env.REACT_APP_PUBLICACIONES_URL}/get_fotos.php?id_perfil=${profileId}`;
      console.log('🌐 Llamando API:', url);
      const res = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
      });
      console.log('📡 Response status:', res.status);
      console.log('📡 Response ok:', res.ok);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      console.log('📦 JSON recibido:', json);
const arr = Array(10).fill(null);
      if (json?.success && json?.data) {
        console.log('✅ API retornó datos exitosos');
        console.log('📊 Total fotos recibidas:', json.total);
        // El backend ya entrega el arreglo de 10 posiciones fijas,
        // respetando el orden real (huecos incluidos donde no hay foto)
        json.data.forEach((foto, idx) => {
          if (foto && idx < 10) {
            const fotoUrl = `${process.env.REACT_APP_UPLOADS_URL}/ID_${profileId}/${foto.fotos}?v=${profileId}`;
            console.log(`📸 Foto ${idx + 1}:`, fotoUrl);
            arr[idx] = {
              src: fotoUrl,
              meta: foto,
            };
          }
        });
      } else {
        console.warn('⚠️ API no retornó datos o success=false');
      }
      console.log('📋 Array final de fotos:', arr);
      setFotos(arr);
      console.log('✅ setFotos ejecutado');
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('❌ Error en fetch:', err);
        console.error('❌ Error message:', err.message);
        console.error('❌ Error stack:', err.stack);
        setError(err.message || 'Error inesperado');
      }
    } finally {
      setLoading(false);
      console.log('🏁 useEffect finalizado, loading=false');
    }
  })();
  return () => {
    console.log('🧹 Cleanup: abortando controller');
    controller.abort();
  };
}, [data]);

  // Cargar estado de "me gusta" (contador + si el viewer actual ya dio like)
  useEffect(() => {
    const idsActivos = fotos
      .filter((f) => f && f.meta && f.meta.estado !== 'pendiente' && f.meta.id)
      .map((f) => f.meta.id);
    if (idsActivos.length === 0) return;
    const userId = localStorage.getItem('userId') || 0;
    const controller = new AbortController();
    (async () => {
      try {
        const identidadParam = isUserLoggedIn
          ? `id_viewer=${userId}`
          : creadorId
          ? `id_creador=${creadorId}`
          : `id_viewer=0`;
        const url = `${process.env.REACT_APP_PUBLICACIONES_URL}/get_estado_likes.php?tipo_contenido=foto&ids=${idsActivos.join(',')}&${identidadParam}`;
        const res = await fetch(url, { signal: controller.signal });
        const json = await res.json();
        if (json?.success && Array.isArray(json.data)) {
          const nuevoEstado = {};
          json.data.forEach((item) => {
            nuevoEstado[item.id] = { liked: item.liked, count: item.likes_count };
          });
          setLikesState((prev) => ({ ...prev, ...nuevoEstado }));
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('❌ Error al cargar estado de likes:', err);
        }
      }
    })();
    return () => controller.abort();
  }, [fotos, isUserLoggedIn, creadorId]);

  const handleToggleLike = async (fotoId) => {
    if (!isUserLoggedIn && !creadorId) {
      setShowLikeLoginBox(true);
      return;
    }
    const userId = localStorage.getItem('userId');
    setLikesState((prev) => {
      const current = prev[fotoId] || { liked: false, count: 0 };
      return {
        ...prev,
        [fotoId]: {
          liked: !current.liked,
          count: current.liked ? Math.max(current.count - 1, 0) : current.count + 1,
        },
      };
    });
    try {
      const formData = new FormData();
      if (isUserLoggedIn) {
        formData.append('ID_U_viewer', userId);
      } else {
        formData.append('ID_U_creador', creadorId);
      }
      formData.append('tipo_contenido', 'foto');
      formData.append('contenido_id', fotoId);
      const response = await fetch(`${process.env.REACT_APP_PUBLICACIONES_URL}/toggle_like.php`, {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      if (result.success) {
        setLikesState((prev) => ({
          ...prev,
          [fotoId]: { liked: result.liked, count: result.likes_count },
        }));
      }
    } catch (err) {
      console.error('❌ Error al dar like a la foto:', err);
    }
  };

  const openPhotoModal = (photoSrc, photoIndex, fotoId) => {
    if (photoSrc) {
      setSelectedPhoto({ src: photoSrc, index: photoIndex, id: fotoId });
      setIsModalOpen(true);
    }
  };
const closeModal = () => {
  setIsModalOpen(false);
  setSelectedPhoto(null);
};
const handlePrevPhoto = (e) => {
  e.stopPropagation();
  let prevIndex = selectedPhoto.index - 1;
  while (prevIndex >= 0 && !fotos[prevIndex]) {
    prevIndex--;
  }
  if (prevIndex >= 0 && fotos[prevIndex]) {
    setSelectedPhoto({ src: fotos[prevIndex].src, index: prevIndex, id: fotos[prevIndex].meta?.id });
  }
};
const handleNextPhoto = (e) => {
  e.stopPropagation();
  let nextIndex = selectedPhoto.index + 1;
  while (nextIndex < fotos.length && !fotos[nextIndex]) {
    nextIndex++;
  }
  if (nextIndex < fotos.length && fotos[nextIndex]) {
    setSelectedPhoto({ src: fotos[nextIndex].src, index: nextIndex, id: fotos[nextIndex].meta?.id });
  }
};
const touchStartX = React.useRef(null);
const handleTouchStart = (e) => {
  touchStartX.current = e.touches[0].clientX;
};
const handleTouchEnd = (e) => {
  if (touchStartX.current === null) return;
  const diff = touchStartX.current - e.changedTouches[0].clientX;
  if (Math.abs(diff) < 50) return;
  if (diff > 0) {
    handleNextPhoto(e);
  } else {
    handlePrevPhoto(e);
  }
  touchStartX.current = null;
};
  const handlePhotoUpload = (index, event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      console.log(`Subiendo foto ${index + 1}:`, file);
      if (onSave) {
        onSave({
          type: 'photo',
          index: index,
          file: file
        });
      }
    } else {
      alert('Por favor selecciona un archivo de imagen válido');
    }
  };
const handlePhotoDelete = async (index) => {
  if (!window.confirm('¿Estás seguro de que quieres eliminar esta foto?')) {
    return;
  }
  const fotoItem = fotos[index];
  if (!fotoItem?.meta?.fotos) {
    alert('No se puede identificar el archivo a eliminar');
    return;
  }
  try {
    const profileId = data?.id ?? data?.profile_id ?? data?.perfil_id;
    const response = await fetch(`${process.env.REACT_APP_PUBLICACIONES_URL}/delete_media.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tipo: 'foto',
        id_perfil: profileId,
        nombre_archivo: fotoItem.meta.fotos
      })
    });
    const result = await response.json();
    if (result.success) {
      // Actualizar el estado local
      const nuevasFotos = [...fotos];
      nuevasFotos[index] = null;
      setFotos(nuevasFotos);
      alert('Foto eliminada exitosamente');
      // Notificar al componente padre si existe
      if (onSave) {
        onSave({
          type: 'photo_delete',
          index: index,
          success: true
        });
      }
    } else {
      throw new Error(result.error || 'Error al eliminar');
    }
  } catch (error) {
    console.error('Error al eliminar foto:', error);
    alert('Error al eliminar la foto: ' + error.message);
  }
};
  console.log('🎨 Renderizando PerfilImagen');
  console.log('🎨 Estado actual - loading:', loading, 'error:', error, 'fotos con contenido:', fotos.filter(f => f !== null).length);
  return (
    <div className="fotos-section">
      <h3 className="fotos-title">Fotos</h3>
      {loading && <div className="fotos-loading">Cargando fotos...</div>}
      {error && (
        <div className="fotos-error">
          Ocurrió un problema al cargar las fotos: {error}
        </div>
      )}
      <div className="fotos-gallery-content">
        {Array.from({ length: 10 }, (_, i) => {
          const item = fotos[i];
          console.log(`🖼️ Renderizando slot ${i + 1}, item:`, item);
          const esActiva = item?.src && item?.meta?.estado !== 'pendiente';
          const fotoId = item?.meta?.id;
          const likeInfo = fotoId ? (likesState[fotoId] || { liked: false, count: 0 }) : { liked: false, count: 0 };
          return (
            <div key={i} className="photo-gallery-item">
{item?.src ? (
                <>
                  <div className="photo-container">
                  {item?.meta?.estado === 'pendiente' ? (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      background: 'linear-gradient(145deg, rgba(106,23,16,0.95), rgba(60,10,5,0.95))',
                      gap: '10px',
                      position: 'absolute',
                      top: 0,
                      left: 0
                    }}>
                      <i className="fas fa-hourglass-half fa-spin" style={{ fontSize: '2.5em', color: '#FFD700' }}></i>
                      <span style={{
                        fontFamily: "'Arial Black', Arial, sans-serif",
                        fontSize: '0.75em',
                        fontWeight: '900',
                        color: '#FFD700',
                        textAlign: 'center',
                        padding: '0 8px',
                        lineHeight: '1.4'
                      }}>
                        PENDIENTE<br/>A REVISIÓN
                      </span>
                    </div>
                  ) : (
                    <>
                      <img
                        src={item.src}
                        alt={`Foto ${i + 1}`}
                        className="photo-thumbnail"
                        onClick={() => openPhotoModal(item.src, i, fotoId)}
                        onError={(e) => {
                          console.error(`❌ Error al cargar imagen ${i + 1}:`, item.src);
                          console.error('❌ Error event:', e);
                        }}
                        onLoad={() => console.log(`✅ Imagen ${i + 1} cargada correctamente`)}
                      />
                      <div
                        className="photo-overlay"
                        onClick={() => openPhotoModal(item.src, i, fotoId)}
                      >
                        <i className="fas fa-search-plus photo-zoom-icon"></i>
                      </div>
                      {isEditMode && (
                        <div className="photo-edit-controls">
                          <button
                            className="photo-delete-btn"
                            onClick={() => handlePhotoDelete(i)}
                            title="Eliminar foto"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  </div>
                  {esActiva && (
                    <div className="like-badge-row">
                      <LikeBadge
                        liked={likeInfo.liked}
                        count={likeInfo.count}
                        onClick={() => handleToggleLike(fotoId)}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="photo-placeholder">
                  <img
                    src={`${process.env.REACT_APP_UPLOADS_URL}/logotipo_new8.jpg`}
                    alt="Ulalaaa Logo"
                    style={{ width: '60%', height: '60%', objectFit: 'contain', marginBottom: '8px' }}
                  />
                  <span className="photo-placeholder-text">Foto {i + 1}</span>
                  {isEditMode && (
                    <div className="photo-upload-area">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePhotoUpload(i, e)}
                        className="photo-file-input"
                        id={`photo-upload-${i}`}
                      />
                      <label
                        htmlFor={`photo-upload-${i}`}
                        className="photo-upload-label"
                      >
                        <i className="fas fa-upload"></i>
                        Subir Foto
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
{isModalOpen && selectedPhoto && (
  <div className="photo-modal" onClick={closeModal}>
    <div className="photo-modal-content" onClick={(e) => e.stopPropagation()} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <span className="close-photo-modal" onClick={closeModal}>
        &times;
      </span>
      {/* Flecha izquierda */}
      {selectedPhoto.index > 0 && fotos.slice(0, selectedPhoto.index).some(f => f) && (
        <button className="photo-nav-btn photo-nav-prev" onClick={handlePrevPhoto}>
          <i className="fas fa-chevron-left"></i>
        </button>
      )}
      <img
        src={selectedPhoto.src}
        alt={`Foto ${selectedPhoto.index + 1}`}
        className="modal-photo"
      />
      {/* Flecha derecha */}
      {selectedPhoto.index < fotos.length - 1 && fotos.slice(selectedPhoto.index + 1).some(f => f) && (
        <button className="photo-nav-btn photo-nav-next" onClick={handleNextPhoto}>
          <i className="fas fa-chevron-right"></i>
        </button>
      )}
      {selectedPhoto.id && (
        <div className="like-badge-row like-badge-row-modal">
          <LikeBadge
            liked={(likesState[selectedPhoto.id] || {}).liked || false}
            count={(likesState[selectedPhoto.id] || {}).count || 0}
            onClick={() => handleToggleLike(selectedPhoto.id)}
          />
        </div>
      )}
      <div className="photo-modal-info">
        <p>Foto {selectedPhoto.index + 1}</p>
      </div>
    </div>
  </div>
)}
      {showLikeLoginBox && (
        <div className="seguir-login-overlay" onClick={() => setShowLikeLoginBox(false)}>
          <div className="login-required-box" onClick={(e) => e.stopPropagation()}>
            <i className="fas fa-lock"></i>
            <h3>Para dar me gusta debes iniciar sesión</h3>
            <button
              className="btn-inicia-sesion"
              onClick={() => {
                const profileId = data?.id ?? data?.profile_id ?? data?.perfil_id;
                localStorage.setItem('returnToProfile', profileId);
                navigate('/acceso_viewer');
              }}
            >
              INICIA SESIÓN
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
export default PerfilImagen;
